import { useCallback, useEffect, useState } from "react";

import {
  clearSessionStorageItem,
  getSessionStorageItem,
  setSessionStorageItem,
} from "@/lib/storage";

/**
 * @template T
 *
 * Read and save the value from sessionStorage based on item path.
 * @param {string} path Path to sessionStorage item.
 * @param {T} initialValue Initial Value to save to sessionStorage item.
 *
 * @returns An array of:
 *   1. value: current sessionStorage item value
 *   1. writeValue: function to update sessionStorage item
 *   1. clearValue: function to remove sessionStorage item
 */
export const useSessionStorage = <T>(path: string, initialValue?: T) => {
  const [value, setValue] = useState(getSessionStorageItem<T>(path) || initialValue);

  const writeValue = useCallback(
    (_value: T) => {
      setValue(_value);
      setSessionStorageItem(path, _value);
    },
    [path],
  );

  // Run only on init to set initialValue if necessary.
  useEffect(() => {
    const storageValue = getSessionStorageItem<T>(path);
    // If storage value IS undefined but initialValue IS NOT - set the LS item with initialValue
    if (storageValue === undefined && initialValue !== undefined) {
      writeValue(initialValue);
    }
  }, [path, initialValue, writeValue]);

  const clearValue = useCallback(() => {
    setValue(undefined);
    clearSessionStorageItem(path);
  }, [path]);

  useEffect(() => {
    const handleEvent = () => {
      setValue(getSessionStorageItem<T>(path));
    };
    window.addEventListener("storage", handleEvent);
    return () => {
      window.removeEventListener("storage", handleEvent);
    };
  }, [path]);

  return [value, writeValue, clearValue] as const;
};
